/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LEARNER_DOMAIN } from "../../lms/_learnerConfig";

// ─────────────────────────────────────────────────────────────────────────────
// Super-admin "Learning feature" org fields, shared by the create modal and the
// edit brand tab (one implementation, two hosts). Controlled-emitter: manages
// its own inputs seeded from `initial` and pushes the current values + validity
// up via `onChange`. The host owns FormData building + dirty/partial-update.
//
//   lmsEnabled       → toggle; when OFF the short name is hidden
//   onlyLmsEnabled   → nested checkbox; forced OFF when lmsEnabled is OFF
//   lmsShortName     → path segment; auto-lowercased; regex-validated;
//                      required only when enabled; live path-based URL preview
//
// NOTE: neither "Approved Email Domains" (lmsAutoApproveDomains) NOR the
// "Default Region" (lmsDefaultRegion) are here — both are managed by the org
// ADMIN (LMS → Settings), not the super-admin provisioning form. The Default
// Region in particular must be validated against the org's own configured
// Regions, which only the admin settings page has access to.
// ─────────────────────────────────────────────────────────────────────────────

// 3–30 chars, lowercase letters/digits/hyphen, no leading/trailing hyphen.
export const SHORTNAME_RE = /^[a-z0-9](?:[a-z0-9-]{1,28})[a-z0-9]$/;

const LmsOrgFields = ({ initial, serverShortNameError, onChange }) => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(!!initial?.lmsEnabled);
  const [onlyLmsEnabled, setOnlyLmsEnabled] = useState(
    !!initial?.lmsEnabled && !!initial?.onlyLmsEnabled
  );
  const [shortName, setShortName] = useState(initial?.lmsShortName || "");
  const [touched, setTouched] = useState(false);

  // Re-seed if the host swaps in fresh data (edit page loads async). Keyed on
  // primitive values, NOT the `initial` object identity — hosts recreate that
  // object every render, so an identity dep would refire endlessly and fight the
  // onChange emit below.
  useEffect(() => {
    setEnabled(!!initial?.lmsEnabled);
    setOnlyLmsEnabled(
      !!initial?.lmsEnabled && !!initial?.onlyLmsEnabled
    );
    setShortName(initial?.lmsShortName || "");
  }, [
    initial?.lmsEnabled,
    initial?.onlyLmsEnabled,
    initial?.lmsShortName,
  ]);

  const shortNameValid = SHORTNAME_RE.test(shortName);

  // Push current state up whenever anything changes.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    onChangeRef.current?.({
      lmsEnabled: enabled,
      onlyLmsEnabled: enabled && onlyLmsEnabled,
      lmsShortName: shortName,
      shortNameValid,
    });
  }, [enabled, onlyLmsEnabled, shortName, shortNameValid]);

  const onEnabledChange = (checked) => {
    setEnabled(checked);
    if (!checked) setOnlyLmsEnabled(false);
  };

  const onShortName = (v) => {
    // Auto-lowercase; drop spaces/underscores as the user types.
    setShortName(v.toLowerCase().replace(/[\s_]/g, ""));
    setTouched(true);
  };

  const localErr =
    enabled && touched && shortName && !shortNameValid
      ? t(
          "superadmin.lms.shortNameInvalid",
          "Use 3–30 lowercase letters, numbers or hyphens; no spaces and no leading/trailing hyphen."
        )
      : "";
  const shortNameErr = serverShortNameError || localErr;

  const preview = `${LEARNER_DOMAIN}/e-learning/${shortName || t("superadmin.lms.shortNamePlaceholder", "your-name")}`;

  return (
    <div className="lms-org-fields col-12 px-2">
      <div className="form-own-group toggle-row">
        <label className="form-own-label mb-0" htmlFor="lmsEnabledSwitch">
          {t(
            "superadmin.lms.enableLabel",
            "Enable e-learning Platform (LMS)"
          )}
        </label>
        <input
          className="toggle-input"
          type="checkbox"
          id="lmsEnabledSwitch"
          data-testid="lms-enabled-toggle"
          checked={enabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
        />
        <label className="toggle-label" htmlFor="lmsEnabledSwitch">
          {t("superadmin.addOrg.toggleLabel", "Enable")}
        </label>
      </div>

      {enabled && (
        <>
          <div className="lms-only-option">
            <div className="lms-only-option-control">
              <input
                className="lms-only-option-checkbox"
                type="checkbox"
                id="onlyLmsEnabled"
                data-testid="only-lms-enabled-checkbox"
                checked={onlyLmsEnabled}
                onChange={(e) => setOnlyLmsEnabled(e.target.checked)}
              />
              <label className="lms-only-option-label" htmlFor="onlyLmsEnabled">
                {t("superadmin.lms.onlyLmsLabel", "LMS Only Organisation")}
              </label>
            </div>
            <p className="lms-only-option-help">
              {t(
                "superadmin.lms.onlyLmsHelp",
                "This organisation will use only the e-learning Management System. All EC Card platform features (Digital Card, Physical Card, Wallet, etc.) will be disabled."
              )}
            </p>
          </div>

          <div className="lms-org-fields-inner">
            <div className="form-own-group">
              <label htmlFor="lmsShortName" className="form-own-label">
                {t("superadmin.lms.shortNameLabel", "Short Name")}{" "}
                <span className="color-red">*</span>
              </label>
              <input
                id="lmsShortName"
                name="lmsShortName"
                className={`form-control ${shortNameErr ? "is-invalid" : ""}`}
                value={shortName}
                autoComplete="off"
                spellCheck={false}
                placeholder={t("superadmin.lms.shortNamePlaceholder", "your-name")}
                aria-invalid={!!shortNameErr}
                aria-describedby="lmsShortName-preview lmsShortName-err"
                onChange={(e) => onShortName(e.target.value)}
              />
              <div id="lmsShortName-preview" className="lms-org-preview">
                <i className="fas fa-globe"></i>{" "}
                <span className={shortName && shortNameValid ? "ok" : ""}>
                  Learner URL : {preview}
                </span>
              </div>
              {shortNameErr && (
                <div id="lmsShortName-err" className="error-text" role="alert">
                  <span>{shortNameErr}</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LmsOrgFields;
